import { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import brand from "@/config/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  project_type: "",
  existing_website: "",
  comments: "",
  timeframe: "",
  website: "", // honeypot
};

const inputCls =
  "w-full rounded-xl border border-taupe/25 bg-white/60 px-4 py-3 font-sans text-sm text-charcoal placeholder:text-taupe/50 focus:outline-none focus:ring-2 focus:ring-turquoise/50 focus:border-turquoise transition-shadow";
const labelCls = "block text-xs font-sans uppercase tracking-[0.14em] font-semibold text-taupe mb-2";

const InquiryForm = ({ preset = "", testPrefix = "inquiry", onClose = null }) => {
  const [form, setForm] = useState({ ...EMPTY, project_type: preset });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/inquiries`, form);
      if (res.data?.status === "success") {
        setStatus("success");
        setForm({ ...EMPTY });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="text-center py-10" data-testid={`${testPrefix}-success`}>
        <CheckCircle2 className="mx-auto text-turquoise-deep mb-5" size={52} strokeWidth={1.5} />
        <h3 className="font-serif text-2xl sm:text-3xl text-charcoal mb-3">
          Thank you — we've received your inquiry.
        </h3>
        <p className="font-sans text-sm text-taupe max-w-md mx-auto leading-relaxed">
          We'll review your details and get back to you within one business day with next steps.
        </p>
        {onClose && (
          <button
            data-testid={`${testPrefix}-close-button`}
            onClick={onClose}
            className="mt-8 bg-charcoal text-cream px-8 py-3 rounded-full font-sans text-sm font-medium hover:bg-coal transition-colors"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid={`${testPrefix}-form`}>
      {/* honeypot */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute -left-[9999px] opacity-0 h-0 w-0"
      />

      <div>
        <label className={labelCls} htmlFor={`${testPrefix}-name`}>Name *</label>
        <input id={`${testPrefix}-name`} data-testid={`${testPrefix}-name-input`} required minLength={2} className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Smith" />
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testPrefix}-business`}>Business Name *</label>
        <input id={`${testPrefix}-business`} data-testid={`${testPrefix}-business-input`} required className={inputCls} value={form.business_name} onChange={set("business_name")} placeholder="Smith's Landscaping" />
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testPrefix}-email`}>Email *</label>
        <input id={`${testPrefix}-email`} data-testid={`${testPrefix}-email-input`} required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@business.com" />
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testPrefix}-phone`}>Phone (optional)</label>
        <input id={`${testPrefix}-phone`} data-testid={`${testPrefix}-phone-input`} type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
      </div>
      <div className="sm:col-span-2">
        <span className={labelCls}>Type of Project *</span>
        <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Type of project">
          {brand.projectTypes.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`${testPrefix}-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setForm((f) => ({ ...f, project_type: t }))}
              aria-pressed={form.project_type === t}
              className={`px-5 py-2.5 rounded-full font-sans text-sm border transition-colors duration-300 ${
                form.project_type === t
                  ? "bg-turquoise text-charcoal border-turquoise font-medium"
                  : "border-taupe/30 text-taupe hover:border-turquoise hover:text-turquoise-deep"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testPrefix}-url`}>Existing Website (optional)</label>
        <input id={`${testPrefix}-url`} data-testid={`${testPrefix}-website-input`} type="url" className={inputCls} value={form.existing_website} onChange={set("existing_website")} placeholder="https://" />
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testPrefix}-timeframe`}>Desired Timeline</label>
        <select id={`${testPrefix}-timeframe`} data-testid={`${testPrefix}-timeframe-select`} className={inputCls} value={form.timeframe} onChange={set("timeframe")}>
          <option value="">Select a timeframe</option>
          {brand.timeframeOptions.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} htmlFor={`${testPrefix}-comments`}>Project Description *</label>
        <textarea id={`${testPrefix}-comments`} data-testid={`${testPrefix}-comments-input`} required rows={4} className={`${inputCls} resize-none`} value={form.comments} onChange={set("comments")} placeholder="Tell us about your business, goals, or ideas…" />
      </div>

      {status === "error" && (
        <div data-testid={`${testPrefix}-error`} className="sm:col-span-2 flex items-center gap-3 rounded-xl border border-turquoise/40 bg-champagne/20 px-4 py-3">
          <AlertCircle className="text-turquoise-deep shrink-0" size={18} />
          <p className="font-sans text-sm text-charcoal">
            Something went wrong and your inquiry was not sent. Please try again, or email us
            directly at {brand.contact.email}.
          </p>
        </div>
      )}

      <div className="sm:col-span-2">
        <button
          data-testid={`${testPrefix}-submit-button`}
          type="submit"
          disabled={status === "sending"}
          className="w-full bg-turquoise text-charcoal hover:bg-turquoise-dark px-8 py-4 rounded-full font-sans font-semibold text-base shadow-lg hover:shadow-xl transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0 flex items-center justify-center gap-2"
        >
          {status === "sending" && <Loader2 className="animate-spin" size={18} />}
          {status === "sending" ? "Sending…" : "Request My Quote"}
        </button>
      </div>
    </form>
  );
};

export default InquiryForm;
